import type { LoginCredentials, SessionPayload } from './auth.models';

export const INVALID_CREDENTIALS_MESSAGE = 'Email o contraseña inválidos.';
export const UNEXPECTED_LOGIN_ERROR_MESSAGE = 'No pudimos iniciar sesión. Probá nuevamente en unos instantes.';

const APP_ROLES = new Set(['SUPER_ADMIN', 'STORE_ADMIN', 'EMPLOYEE']);

const isStoreMembershipList = (value: unknown): boolean => {
  return (
    Array.isArray(value) &&
    value.every(
      (membership) =>
        membership &&
        typeof membership === 'object' &&
        typeof (membership as Record<string, unknown>).id === 'string' &&
        typeof (membership as Record<string, unknown>).code === 'string' &&
        typeof (membership as Record<string, unknown>).name === 'string' &&
        typeof (membership as Record<string, unknown>).isActive === 'boolean'
    )
  );
};

export const normalizeLoginCredentials = (credentials: LoginCredentials): LoginCredentials => ({
  email: credentials.email.trim().toLowerCase(),
  password: credentials.password
});

export const isSessionPayload = (value: unknown): value is SessionPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  const user = payload.user;
  const actor = payload.actor;

  if (!user || typeof user !== 'object' || !actor || typeof actor !== 'object') {
    return false;
  }

  const sessionUser = user as Record<string, unknown>;
  const sessionActor = actor as Record<string, unknown>;

  return (
    typeof sessionUser.id === 'string' &&
    typeof sessionUser.email === 'string' &&
    typeof sessionUser.name === 'string' &&
    typeof sessionUser.isActive === 'boolean' &&
    typeof sessionUser.features === 'object' &&
    sessionUser.features !== null &&
    APP_ROLES.has(String(sessionUser.role)) &&
    (sessionUser.assignedStoreId === null || typeof sessionUser.assignedStoreId === 'string') &&
    (sessionUser.activeStoreId === null || typeof sessionUser.activeStoreId === 'string') &&
    isStoreMembershipList(sessionUser.storeMemberships) &&
    typeof sessionActor.sessionId === 'string' &&
    typeof sessionActor.userId === 'string' &&
    typeof sessionActor.email === 'string' &&
    typeof sessionActor.name === 'string' &&
    typeof sessionActor.isActive === 'boolean' &&
    typeof sessionActor.features === 'object' &&
    sessionActor.features !== null &&
    typeof sessionActor.expiresAt === 'string' &&
    typeof sessionActor.createdAt === 'string' &&
    typeof sessionActor.updatedAt === 'string' &&
    APP_ROLES.has(String(sessionActor.role)) &&
    (sessionActor.assignedStoreId === null || typeof sessionActor.assignedStoreId === 'string') &&
    (sessionActor.activeStoreId === null || typeof sessionActor.activeStoreId === 'string') &&
    isStoreMembershipList(sessionActor.storeMemberships)
  );
};

export const resolveReturnUrl = (returnUrl: string | null | undefined): string => {
  if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
    return '/';
  }

  return returnUrl.startsWith('/login') ? '/' : returnUrl;
};

export const getLoginRedirectQuery = (returnUrl: string | null | undefined): Record<string, string> => {
  const safeReturnUrl = resolveReturnUrl(returnUrl);

  return safeReturnUrl === '/' ? {} : { returnUrl: safeReturnUrl };
};

export const mapLoginError = (error: unknown): string => {
  if (isInvalidCredentialsError(error)) {
    return INVALID_CREDENTIALS_MESSAGE;
  }

  return UNEXPECTED_LOGIN_ERROR_MESSAGE;
};

const isInvalidCredentialsError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    status?: unknown;
    error?: {
      code?: unknown;
      message?: unknown;
    };
  };

  const code = typeof candidate.error?.code === 'string' ? candidate.error.code : '';
  const message = typeof candidate.error?.message === 'string' ? candidate.error.message.toLowerCase() : '';

  return (
    candidate.status === 400 ||
    candidate.status === 401 ||
    code === 'INVALID_EMAIL_OR_PASSWORD' ||
    code === 'INVALID_CREDENTIALS' ||
    message.includes('invalid email or password')
  );
};
